// client/src/components/pages/LexiStar/Dashboard/analyticsAtoms.tsx
//
// Presentational pieces shared by the LexiStar numbers screens (ST-00188 M4-D:
// management dashboard, KPIs, dwell, idling heatmap). ST-00207 (phone pass): PeriodPicker has a
// phone form (the date field alone) and "How is this counted?" is a 44 px target on a phone or a
// touch screen (.touch-target; no change with a mouse). LS-09 §8: charcoal +
// white + one accent (the brand teal, `primary`), the §4 status palette is
// never used here (colour is state, and only state — deltas are words and
// arrows, not red/green), tabular numerals in tables. LS-09 §1.1: provisional
// figures are marked provisional, every figure shows its provenance and its
// counting rule on demand.

import React, { useId } from 'react';
import { SegmentedControl, UnifiedDateRangePicker } from '../../../../types/commonComponents';
import { type Delta, deltaOf, type Better, type Unit } from './metricFormat';
import {
  type Period,
  type PresetId,
  PRESETS,
  matchPreset,
  presetPeriod,
  parseDay,
  dayString,
} from './periodLogic';

export const cardClasses = 'bg-surface rounded-xl border border-slate-200 shadow-sm';

// ── Period state chip ───────────────────────────────────────────────────────

export type PeriodState = 'provisional' | 'final';

const STATE_HELP: Record<PeriodState, string> = {
  final:
    'Final — the period is closed: its trips are complete or signed off and no review items are open. This figure will not change.',
  provisional:
    'Provisional — late data, open review items or trips still running can change this figure. It becomes final a few days after the period ends.',
};

/** Final: solid neutral chip with a tick. Provisional: dashed outline (LS-09 §1.1 "honest about staleness"). */
export function StateChip({ state, className = '' }: { state: PeriodState | null | undefined; className?: string }) {
  if (!state) return null;
  const provisional = state === 'provisional';
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-px rounded text-[11px] font-medium leading-4 whitespace-nowrap ${
        provisional
          ? 'border border-dashed border-slate-400 text-slate-600'
          : 'border border-slate-200 bg-slate-100 text-slate-700'
      } ${className}`}
      title={STATE_HELP[state]}
      data-state={state}
    >
      <span aria-hidden="true">{provisional ? '◌' : '✓'}</span>
      {provisional ? 'Provisional' : 'Final'}
    </span>
  );
}

// ── Provenance (LS-09 §3: ● Measured ◐ Derived ○ Estimated) ─────────────────

type ProvKind = 'measured' | 'derived' | 'estimated' | 'mixed';

const PROVENANCE: Record<string, { kind: ProvKind; label: string; help: string }> = {
  measured: { kind: 'measured', label: 'Measured', help: 'Measured — from the vehicle, the weighbridge or a signed record.' },
  derived: { kind: 'derived', label: 'Derived', help: 'Derived — calculated from GPS positions, fuel-level changes or matched tickets.' },
  estimated: { kind: 'estimated', label: 'Estimated', help: 'Estimated — from norms, rate cards and prices; labelled estimated everywhere.' },
  mixed: { kind: 'mixed', label: 'Mixed', help: 'Mixed — some trips measured, others derived or estimated. The provenance mix is in the drill-through list.' },
  measured_or_derived: { kind: 'derived', label: 'Measured or derived', help: 'Measured where the vehicle reports it, otherwise derived from GPS.' },
};

/**
 * The provenance glyph drawn as a small SVG (the ● ◐ ○ characters render as
 * chevron-like slivers in some UI fonts): full disc = measured, half disc =
 * derived, ring = estimated, ring with a dot = mixed. Colour-neutral; the
 * tooltip and the accessible name carry the words.
 */
export function ProvenanceMark({ provenance, className = '' }: { provenance?: string | null; className?: string }) {
  const meta = provenance ? PROVENANCE[String(provenance).toLowerCase()] : undefined;
  if (!meta) return null;
  return (
    <span className={`inline-flex align-middle text-slate-500 cursor-help ${className}`} title={meta.help} role="img" aria-label={meta.label} data-provenance={meta.kind}>
      <svg width="9" height="9" viewBox="0 0 10 10" aria-hidden="true" className="overflow-visible">
        {meta.kind === 'measured' && <circle cx="5" cy="5" r="4" fill="currentColor" />}
        {meta.kind === 'derived' && (
          <>
            <circle cx="5" cy="5" r="3.75" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <path d="M5 1.25 A3.75 3.75 0 0 0 5 8.75 Z" fill="currentColor" />
          </>
        )}
        {meta.kind === 'estimated' && <circle cx="5" cy="5" r="3.75" fill="none" stroke="currentColor" strokeWidth="1.5" />}
        {meta.kind === 'mixed' && (
          <>
            <circle cx="5" cy="5" r="3.75" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <circle cx="5" cy="5" r="1.5" fill="currentColor" />
          </>
        )}
      </svg>
    </span>
  );
}

/** Legend line for the four marks. */
export function ProvenanceLegend({ className = '' }: { className?: string }) {
  return (
    <span className={`inline-flex flex-wrap items-center gap-x-3 gap-y-1 ${className}`}>
      {(['measured', 'derived', 'estimated', 'mixed'] as const).map((k) => (
        <span key={k} className="inline-flex items-center gap-1">
          <ProvenanceMark provenance={k} />
          {PROVENANCE[k]?.label.toLowerCase()}
        </span>
      ))}
    </span>
  );
}

// ── Delta against the previous period ───────────────────────────────────────

/** "▲ 12% · worse" in neutral ink — the arrow and the word carry it, never colour. */
export function DeltaText({
  current,
  previous,
  unit,
  better,
  suffix = 'vs previous',
  className = '',
}: {
  current: unknown;
  previous: unknown;
  unit: Unit;
  better: Better;
  suffix?: string;
  className?: string;
}) {
  const d: Delta | null = deltaOf(current, previous, unit, better);
  if (!d) return <span className={`text-xs text-slate-400 ${className}`}>No previous figure</span>;
  const arrow = d.direction === 'up' ? '▲' : d.direction === 'down' ? '▼' : '＝';
  return (
    <span className={`inline-flex items-baseline gap-1 text-xs text-slate-600 tabular-nums ${className}`} data-judgement={d.judgement ?? 'none'}>
      <span aria-hidden="true" className="text-[10px] text-slate-500">
        {arrow}
      </span>
      <span className={d.judgement === 'worse' ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}>{d.text}</span>
      {d.judgement && <span className="text-slate-500">{d.judgement}</span>}
      {suffix && <span className="text-slate-400">{suffix}</span>}
    </span>
  );
}

// ── Sparkline ───────────────────────────────────────────────────────────────

export interface SparkPoint {
  value: number | null;
  state?: PeriodState | null;
  label?: string;
}

/**
 * 2 px line in the de-emphasis ink, the latest point in the accent with a
 * surface ring (dataviz stat-tile contract). Provisional stretches are dashed.
 * Nulls break the line. Decorative for AT — the tile states the figure — but
 * it carries a text summary for anyone who asks.
 */
export function Sparkline({
  points,
  width = 112,
  height = 28,
  label,
  className = '',
}: {
  points: SparkPoint[];
  width?: number;
  height?: number;
  label?: string;
  className?: string;
}) {
  const values = points.map((p) => p.value).filter((v): v is number => v !== null && Number.isFinite(v));
  if (values.length < 2) {
    return <span className={`inline-block text-[11px] text-slate-400 ${className}`} style={{ width }}>{values.length ? '' : 'No trend'}</span>;
  }
  const pad = 3;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const n = points.length;
  const x = (i: number) => pad + (n === 1 ? 0 : (i * (width - pad * 2)) / (n - 1));
  const y = (v: number) => height - pad - ((v - min) / span) * (height - pad * 2);

  // Split into runs of (final | provisional) so provisional stretches draw dashed.
  const segments: { d: string; provisional: boolean }[] = [];
  let current: { d: string; provisional: boolean } | null = null;
  let prev: { i: number; v: number } | null = null;
  points.forEach((p, i) => {
    if (p.value === null || !Number.isFinite(p.value)) {
      current = null;
      prev = null;
      return;
    }
    const provisional = p.state === 'provisional';
    const pt = `${x(i).toFixed(1)},${y(p.value).toFixed(1)}`;
    if (!current || current.provisional !== provisional) {
      const start: string = prev ? `M${x(prev.i).toFixed(1)},${y(prev.v).toFixed(1)} L${pt}` : `M${pt}`;
      current = { d: start, provisional };
      segments.push(current);
    } else {
      current.d += ` L${pt}`;
    }
    prev = { i, v: p.value };
  });

  let lastIndex = -1;
  for (let i = points.length - 1; i >= 0; i--) {
    const v = points[i]?.value;
    if (v !== null && v !== undefined && Number.isFinite(v)) {
      lastIndex = i;
      break;
    }
  }
  const lastValue = points[lastIndex]?.value ?? null;
  const summary =
    label ??
    `Trend over ${n} periods: from ${values[0]?.toLocaleString('en-GB')} to ${values[values.length - 1]?.toLocaleString('en-GB')}`;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={`overflow-visible text-slate-400 ${className}`}
      role="img"
      aria-label={summary}
      data-testid="sparkline"
      data-points={n}
    >
      <title>{summary}</title>
      {segments.map((s, i) => (
        <path
          key={i}
          d={s.d}
          fill="none"
          stroke="currentColor"
          strokeWidth={s.provisional ? 1.5 : 2}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray={s.provisional ? '3 2.5' : undefined}
        />
      ))}
      {lastValue !== null && lastIndex >= 0 && (
        <circle
          cx={x(lastIndex)}
          cy={y(lastValue)}
          r={3.5}
          className="fill-primary-500"
          style={{ stroke: 'var(--surface, #fff)' }}
          strokeWidth={2}
        />
      )}
    </svg>
  );
}

// ── Period toolbar (shared pickers; LS-09 §3 presets plus custom) ───────────

export function PeriodPicker({
  period,
  onChange,
  today,
  className = '',
  phone = false,
}: {
  period: Period;
  onChange: (p: Period) => void;
  today?: string;
  className?: string;
  /**
   * ST-00207: the phone layout — the date field alone; its bottom sheet carries the same
   * quick ranges (Last 7 days, Last 30 days, This month, Last month) as the preset buttons.
   */
  phone?: boolean;
}) {
  const preset = matchPreset(period, today);
  const onStart = (d: Date | null) => {
    if (!d) return;
    const from = dayString(d);
    onChange({ from, to: period.to < from ? from : period.to });
  };
  const onEnd = (d: Date | null) => {
    if (!d) return;
    const to = dayString(d);
    onChange({ from: period.from > to ? to : period.from, to });
  };
  if (phone) {
    return (
      <div className={`w-full ${className}`}>
        <UnifiedDateRangePicker
          startDate={parseDay(period.from)}
          endDate={parseDay(period.to)}
          onStartDateChange={onStart}
          onEndDateChange={onEnd}
        />
      </div>
    );
  }
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className}`}>
      <SegmentedControl<PresetId | 'custom'>
        ariaLabel="Period"
        value={preset ?? 'custom'}
        onChange={(id) => id !== 'custom' && onChange(presetPeriod(id, today))}
        options={PRESETS.map((p) => ({ value: p.id, label: p.label, shortLabel: p.shortLabel }))}
      />
      <div className="w-full sm:w-60">
        <UnifiedDateRangePicker
          startDate={parseDay(period.from)}
          endDate={parseDay(period.to)}
          onStartDateChange={onStart}
          onEndDateChange={onEnd}
        />
      </div>
    </div>
  );
}

// ── Small layout pieces ─────────────────────────────────────────────────────

export function SectionTitle({ children, aside, id }: { children: React.ReactNode; aside?: React.ReactNode; id?: string }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
      <h2 id={id} className="text-sm font-semibold text-slate-900 tracking-tight">
        {children}
      </h2>
      {aside}
    </div>
  );
}

/** Skeleton rows (LS-09 §1 "skeletons not spinners"). */
export function SkeletonBlock({ className = 'h-24' }: { className?: string }) {
  return <div className={`animate-pulse rounded-lg bg-slate-100 ${className}`} aria-hidden="true" />;
}

/** "How is this counted?" disclosure carrying the metric definition (LS-09 §1.1 shown workings). */
export function Workings({
  formula,
  description,
  provenanceRule,
  version,
}: {
  formula?: string | null;
  description?: string | null;
  provenanceRule?: string | null;
  version?: number | null;
}) {
  const id = useId();
  if (!formula && !description) return null;
  return (
    <details className="group mt-1 text-[11px] text-slate-500">
      <summary className="touch-target cursor-pointer select-none list-none inline-flex items-center gap-1 hover:text-slate-800 [&::-webkit-details-marker]:hidden" aria-describedby={id}>
        <span aria-hidden="true" className="transition-transform group-open:rotate-90">
          ›
        </span>
        How is this counted?
      </summary>
      <div id={id} className="mt-1.5 rounded-md bg-slate-50 border border-slate-200 p-2 space-y-1 text-slate-700">
        {description && <p>{description}</p>}
        {formula && <p className="font-mono text-[11px]">{formula}</p>}
        {(provenanceRule || version) && (
          <p className="text-slate-500">
            {provenanceRule ? `Provenance: ${provenanceRule.replace(/_/g, ' ')}` : ''}
            {provenanceRule && version ? ' · ' : ''}
            {version ? `definition v${version}` : ''}
          </p>
        )}
      </div>
    </details>
  );
}
